import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { useState, type JSX } from 'react'
import { Control, Controller, FieldValues, type Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { Device } from '@/pages/Device/Device'
import type { Composition } from '@/pages/calibration/composition'
import {
  labelNoErrorColor,
  labelRoot,
  radioGroupLabelSx,
  requiredIndicator,
  selectStandardSx,
} from '@/utils/theme'
import { DeviceRole, DeviceRoleBoxLabel } from '@/pages/calibration/calibration'
import UploadCertificateDialog from '@/pages/calibration/preFlight/certificate/UploadCertificateDialog'
import { isNmdiWithFota } from '@/pages/calibration/version'

const DATE_FORMAT = 'yyyy-MM-dd'

export function showFieldError(fieldState: {
  error?: { type: string; message?: string }
}): string | undefined {
  if (!fieldState.error) return undefined
  // Suppress 'required' errors - asterisk on label handles that visually
  // Show all other errors - wrong value, custom validation, etc.
  if (fieldState.error.type === 'required') return undefined
  return fieldState.error.message
}
export interface SharedPreFlightFieldsProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  control: Control<TFieldValues>
  isSubmitted: boolean
  hasBoard: boolean
  minInstalledDate: Date
  countryItems: { id: string; label: string }[]
  deviceRoles: DeviceRole[]
  deviceRolesLoading: boolean
  hasConfigurableServiceInterval: boolean
  isSaving: boolean
  onCountryChange: (country: string) => void
  onYearsIntervalChange: (years: number) => void
  composition: Composition | null
  device: Device
  setDevice?: (device: Device) => void
  certificateUploadFieldName: Path<TFieldValues>
}

const SharedPreFlightFields = <TFieldValues extends FieldValues>({
  control,
  isSubmitted,
  hasBoard,
  minInstalledDate,
  countryItems,
  deviceRoles,
  deviceRolesLoading,
  hasConfigurableServiceInterval,
  isSaving,
  onCountryChange,
  onYearsIntervalChange,
  composition,
  device,
  setDevice,
  certificateUploadFieldName,
}: SharedPreFlightFieldsProps<TFieldValues>): JSX.Element => {
  const { t } = useTranslation(['app', 'common', 'di'])
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const showUploadCertificate = isNmdiWithFota(composition)

  return (
    <>
      {/* installedDate - only for devices with battery board */}
      {hasBoard && (
        <Controller
          name={'installedDate' as Path<TFieldValues>}
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              type="date"
              label={t('app:device.calibration.preFlightStep.installedDate')}
              fullWidth
              variant="standard"
              slotProps={{
                inputLabel: {
                  shrink: true,
                  sx: {
                    ...labelRoot,
                    ...requiredIndicator,
                    ...labelNoErrorColor,
                    position: 'relative',
                    transform: 'none',
                  },
                },
                htmlInput: {
                  min: format(minInstalledDate, DATE_FORMAT),
                  max: format(new Date(), DATE_FORMAT),
                  autoComplete: 'off',
                },
              }}
              error={isSubmitted && Boolean(fieldState.error)}
              helperText={isSubmitted ? showFieldError(fieldState) : undefined}
              sx={{ mb: 3 }}
            />
          )}
        />
      )}

      {/* Country */}
      <Controller
        name={'country' as Path<TFieldValues>}
        control={control}
        render={({ field, fieldState }) => (
          <FormControl
            fullWidth
            variant="standard"
            sx={{ mb: 2.5, width: '100%' }}
            error={isSubmitted && Boolean(fieldState.error)}
          >
            <InputLabel
              sx={{
                ...labelRoot,
                ...requiredIndicator,
                ...labelNoErrorColor,
                position: 'relative',
                transform: 'none',
              }}
            >
              {t('di:device.country')}
            </InputLabel>
            <Select
              {...field}
              variant="standard"
              sx={selectStandardSx}
              onChange={(e) => {
                field.onChange(e)
                const newCountry = e.target.value
                onCountryChange(newCountry)
                if (setDevice) setDevice({ ...device, country: newCountry })
              }}
            >
              {countryItems.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
            {isSubmitted && showFieldError(fieldState) && (
              <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
            )}
          </FormControl>
        )}
      />

      {/* Device role = enabled only for RSS, disabled for all others */}
      <Controller
        name={'deviceRoleCode' as Path<TFieldValues>}
        control={control}
        render={({ field, fieldState }) => (
          <FormControl
            fullWidth
            variant="standard"
            sx={{ mb: 2.5, width: '100%' }}
            error={isSubmitted && Boolean(fieldState.error)}
          >
            <InputLabel
              sx={{
                ...labelRoot,
                ...requiredIndicator,
                ...labelNoErrorColor,
                position: 'relative',
                transform: 'none',
              }}
            >
              {t('app:device.calibration.preFlightStep.deviceRole')}
            </InputLabel>
            <Select
              {...field}
              variant="standard"
              sx={selectStandardSx}
              disabled={deviceRoles.length <= 1 || deviceRolesLoading}
            >
              {deviceRoles.map((role) => (
                <MenuItem key={role.deviceRoleCode} value={role.deviceRoleCode}>
                  {DeviceRoleBoxLabel(role)}
                </MenuItem>
              ))}
            </Select>
            {isSubmitted && showFieldError(fieldState) && (
              <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
            )}
          </FormControl>
        )}
      />

      {/* Years between maintenance = only for devices with configurable interval */}

      {hasConfigurableServiceInterval && (
        <Controller
          name={'yearsBetweenMaintenance' as Path<TFieldValues>}
          control={control}
          render={({ field, fieldState }) => {
            const hasError = isSubmitted && Boolean(fieldState.error)
            return (
              <FormControl
                component="fieldset"
                error={hasError}
                sx={{ mb: 2.5, width: '100%', display: 'block' }}
              >
                <Typography
                  sx={{
                    ...radioGroupLabelSx,
                    ...requiredIndicator,
                    position: 'relative',
                    transform: 'none',
                  }}
                >
                  {t(
                    'app:device.calibration.preFlightStep.yearsBetweenMaintenance',
                  )}
                </Typography>
                <RadioGroup
                  {...field}
                  onChange={(e) => {
                    field.onChange(e)
                    onYearsIntervalChange(Number.parseInt(e.target.value, 10))
                  }}
                >
                  {['2', '4'].map((val) => (
                    <FormControlLabel
                      key={val}
                      value={val}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: hasError
                              ? 'error.main'
                              : 'rgba(0, 0, 0, 0.54)',
                            '&.Mui-checked': {
                              color: hasError
                                ? 'error.main'
                                : 'rgba(0, 0, 0, 0.87)',
                            },
                          }}
                        />
                      }
                      label={val}
                      disabled={isSaving}
                    />
                  ))}
                </RadioGroup>
                {isSubmitted && showFieldError(fieldState) && (
                  <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
                )}
              </FormControl>
            )
          }}
        />
      )}

      {showUploadCertificate ? (
        <Controller
          name={certificateUploadFieldName}
          control={control}
          render={({ field, fieldState }) => (
            <div style={{ marginBottom: '20px' }}>
              <input
                type="hidden"
                name={field.name}
                value={field.value ? 'true' : 'false'}
                readOnly
              />
              <Typography
                sx={{
                  ...radioGroupLabelSx,
                  ...requiredIndicator,
                  position: 'relative',
                  transform: 'none',
                }}
              >
                Verify the presence of the FOTA certificate
              </Typography>
              <Button
                variant="contained"
                onClick={() => setUploadDialogOpen(true)}
                disabled={isSaving}
                sx={{ mt: 1 }}
              >
                {field.value ? 'Certificate uploaded' : 'Upload certificate'}
              </Button>
              {isSubmitted && showFieldError(fieldState) && (
                <FormHelperText error sx={{ mt: 0, ml: 0 }}>
                  {showFieldError(fieldState)}
                </FormHelperText>
              )}
              <UploadCertificateDialog
                open={uploadDialogOpen}
                setOpen={setUploadDialogOpen}
                onUploaded={() => field.onChange(true)}
              />
            </div>
          )}
        />
      ) : null}
    </>
  )
}

export default SharedPreFlightFields
